/**
 * CourtroomJudgeScoringDialog — form chấm điểm Bán kết/Chung kết inline trong
 * CourtroomPage cho judge. Mỗi match (semifinal) có 2 phiếu tự sinh (1 phiếu/
 * đội) khi courtroom kết thúc — judge nhập 5 tiêu chí, sum tự động, Save +
 * Submit.
 *
 * 2026-05-25: tách khỏi ScoringPage cồng kềnh để judge có UX nhanh — không
 * weight, không AI suggest (đúng spec BTC), không cần navigate khỏi
 * courtroom. Built-in semifinal rubric (5 tiêu chí, max 100).
 */
import { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { Assessment, CheckCircle, Lock } from '@mui/icons-material'

import {
  useGetScoresQuery,
  useUpdateScoreMutation,
  useSubmitScoreMutation,
  type Score,
} from '../store/api/scoringApi'
import { useGetTeamsQuery } from '../store/api/teamsApi'

// ────────────────────────────────────────────────────────────────────────────
// Rubric chính thức Vòng Bán kết (đồng bộ với BE
// ScoringService.get_semifinal_rubric — xem scoring_service.py:1322).
// Final dùng cùng 5 tiêu chí (BTC chưa ban hành rubric Chung kết riêng).
// ────────────────────────────────────────────────────────────────────────────
interface CriterionDef {
  key: string
  name: string
  description: string
  max_score: number
}

const SEMIFINAL_CRITERIA: CriterionDef[] = [
  {
    key: 'I_evidence_present',
    name: 'I. Kỹ năng xuất trình và bảo vệ chứng cứ',
    description:
      'Tư duy "lọc" và sử dụng chứng cứ (Điều 93, 94 BLTTDS). Lập luận tính khách quan/hợp pháp/liên quan; loại bỏ chứng cứ nhiễu; giải trình nguồn gốc khi Thẩm phán yêu cầu.',
    max_score: 20,
  },
  {
    key: 'II_evidence_rebuttal',
    name: 'II. Kỹ năng tiếp cận và phản bác chứng cứ đối phương',
    description:
      'Tư duy phản biện, ứng biến. Phát hiện điểm yếu chứng cứ đối phương; câu hỏi chất vấn sắc bén làm suy yếu giá trị chứng minh.',
    max_score: 20,
  },
  {
    key: 'III_negotiation',
    name: 'III. Kỹ năng đàm phán và tham gia hòa giải',
    description:
      'Nghệ thuật thương lượng Win-Win (Điều 205 BLTTDS). Phương án hòa giải khả thi, nhượng bộ nhưng giữ cốt lõi; dùng đòn bẩy pháp lý; thiện chí hỗ trợ Thẩm phán.',
    max_score: 25,
  },
  {
    key: 'IV_law_application',
    name: 'IV. Kiến thức pháp luật và áp dụng luật',
    description:
      'Viện dẫn chính xác Luật HN&GĐ 2014 (Điều 56, 59, 81, 82) và BLTTDS. Tính toán chia ~30 tỷ và bù trừ nghĩa vụ chính xác tại bàn hòa giải.',
    max_score: 20,
  },
  {
    key: 'V_conduct',
    name: 'V. Tác phong, ngôn ngữ và đạo đức nghề nghiệp',
    description:
      'Ngôn ngữ chuẩn mực, tôn trọng Thẩm phán/đồng nghiệp; không ngắt lời, kiểm soát cảm xúc; phân bổ thời gian hợp lý giữa 2 Luật sư cùng đội.',
    max_score: 15,
  },
]
const TOTAL_MAX = SEMIFINAL_CRITERIA.reduce((s, c) => s + c.max_score, 0) // 100

interface Props {
  open: boolean
  onClose: () => void
  /** Courtroom hiện tại — dùng để filter phiếu theo (case, courtroom, judge). */
  courtroom: {
    id: string
    case_id?: string
    round_id?: string
    round_type?: string
    match_id?: string | null
  } | null
  currentUserId?: string
}

export default function CourtroomJudgeScoringDialog({
  open,
  onClose,
  courtroom,
  currentUserId,
}: Props) {
  const caseId = courtroom?.case_id || ''
  const courtroomId = courtroom?.id || ''
  const roundLabel = courtroom?.round_type === 'final' ? 'Chung kết' : 'Bán kết'

  // Fetch tất cả phiếu của judge cho case, sau đó lọc theo courtroom_id.
  // BE auto-filter judge_id=current_user.id cho judge role (anti-leak phiếu GK
  // khác), nên chỉ cần truyền case_id.
  const { data: allScores = [], isFetching, refetch } = useGetScoresQuery(
    { case_id: caseId },
    { skip: !open || !caseId, pollingInterval: 10000 },
  )
  const { data: teams = [] } = useGetTeamsQuery(undefined, { skip: !open })

  // Phiếu của tôi cho courtroom này (1 phiếu/đội = 2 phiếu cho semifinal match)
  const myScoresForCourtroom = useMemo(
    () =>
      (allScores as Score[]).filter(
        (s) =>
          s.courtroom_id === courtroomId &&
          s.judge_id === currentUserId &&
          (s.submission_type === 'courtroom' ||
            s.round_type === 'semifinal' ||
            s.round_type === 'final'),
      ),
    [allScores, courtroomId, currentUserId],
  )

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Assessment color="primary" />
          <span>Phiếu chấm điểm — Vòng {roundLabel}</span>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          5 tiêu chí, tổng tối đa <b>{TOTAL_MAX}</b> điểm. Hệ thống cộng dồn
          tổng — KHÔNG auto trọng số, KHÔNG AI gợi ý. Sau khi <b>Gửi phiếu</b>,
          phiếu bị khoá; chỉ Admin mở lại với audit log.
        </Alert>

        {isFetching && myScoresForCourtroom.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : myScoresForCourtroom.length === 0 ? (
          <Alert severity="warning">
            Chưa có phiếu chấm cho phiên này. Phiếu được tự tạo khi Chủ tọa
            bấm <b>"Kết thúc phiên"</b>. Nếu phiên đã kết thúc mà vẫn chưa có
            phiếu, vui lòng reload hoặc liên hệ BTC.
          </Alert>
        ) : (
          <Stack spacing={3}>
            {myScoresForCourtroom.map((sc) => {
              const team = teams.find((t) => t.id === sc.team_id)
              return (
                <TeamScorecard
                  key={sc.id}
                  score={sc}
                  teamName={team?.name || sc.team_id?.slice(0, 8) || '—'}
                  onSaved={() => refetch()}
                />
              )
            })}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Đóng</Button>
      </DialogActions>
    </Dialog>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// TeamScorecard — 1 phiếu chấm cho 1 đội: 5 inputs + sum + Save/Submit
// ────────────────────────────────────────────────────────────────────────────
function TeamScorecard({
  score,
  teamName,
  onSaved,
}: {
  score: Score
  teamName: string
  onSaved: () => void
}) {
  const [updateScore, { isLoading: saving }] = useUpdateScoreMutation()
  const [submitScore, { isLoading: submitting }] = useSubmitScoreMutation()

  // Local state cho 5 tiêu chí — init từ score.rubric_scores
  const [scores, setScores] = useState<Record<string, number | ''>>(() => {
    const init: Record<string, number | ''> = {}
    SEMIFINAL_CRITERIA.forEach((c) => {
      const v = (score.rubric_scores || {})[c.key]
      init[c.key] = typeof v === 'number' ? v : ''
    })
    return init
  })
  const [comments, setComments] = useState(score.comments || '')
  const [errMsg, setErrMsg] = useState('')
  const [okMsg, setOkMsg] = useState('')

  // Sync khi score record refetch (vd Admin update từ tab khác)
  useEffect(() => {
    setComments(score.comments || '')
    setScores(() => {
      const init: Record<string, number | ''> = {}
      SEMIFINAL_CRITERIA.forEach((c) => {
        const v = (score.rubric_scores || {})[c.key]
        init[c.key] = typeof v === 'number' ? v : ''
      })
      return init
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [score.id, score.updated_at])

  const total = useMemo(
    () =>
      SEMIFINAL_CRITERIA.reduce((sum, c) => {
        const v = scores[c.key]
        return sum + (typeof v === 'number' ? v : 0)
      }, 0),
    [scores],
  )

  const isLocked = score.status === 'submitted' || !!score.published_at
  const overflows = SEMIFINAL_CRITERIA.filter((c) => {
    const v = scores[c.key]
    return typeof v === 'number' && v > c.max_score
  })

  const buildPayload = () => ({
    rubric_scores: Object.fromEntries(
      SEMIFINAL_CRITERIA.map((c) => [
        c.key,
        typeof scores[c.key] === 'number' ? (scores[c.key] as number) : 0,
      ]),
    ),
    comments: comments.trim(),
  })

  const handleSaveDraft = async () => {
    setErrMsg('')
    setOkMsg('')
    if (overflows.length > 0) {
      setErrMsg(`Có ${overflows.length} tiêu chí vượt quá điểm tối đa.`)
      return
    }
    try {
      await updateScore({ id: score.id, update: buildPayload() }).unwrap()
      setOkMsg('Đã lưu nháp.')
      onSaved()
    } catch (e: any) {
      setErrMsg(e?.data?.detail || e?.message || 'Lưu thất bại')
    }
  }

  const handleSubmit = async () => {
    setErrMsg('')
    setOkMsg('')
    if (overflows.length > 0) {
      setErrMsg(`Có ${overflows.length} tiêu chí vượt quá điểm tối đa.`)
      return
    }
    if (
      !window.confirm(
        `Gửi phiếu chấm cho đội "${teamName}" (Tổng: ${total.toFixed(2)} / ${TOTAL_MAX})?\n\n` +
          'Sau khi gửi phiếu sẽ KHOÁ — chỉ Admin mở lại được kèm lý do (audit log).',
      )
    )
      return
    try {
      // Phải save trước để rubric_scores ghi vào DB, sau đó submit để lock.
      await updateScore({ id: score.id, update: buildPayload() }).unwrap()
      await submitScore(score.id).unwrap()
      setOkMsg('Đã gửi phiếu — đã khoá.')
      onSaved()
    } catch (e: any) {
      setErrMsg(e?.data?.detail || e?.message || 'Gửi phiếu thất bại')
    }
  }

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: isLocked ? 'success.main' : 'divider',
        borderRadius: 2,
        p: 2,
        background: isLocked ? 'rgba(46,125,50,0.04)' : 'transparent',
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5, flexWrap: 'wrap' }}>
        <Typography variant="subtitle1" fontWeight={700}>
          Đội: {teamName}
        </Typography>
        {isLocked ? (
          <Chip
            size="small"
            color="success"
            icon={<Lock fontSize="small" />}
            label={score.published_at ? 'Đã công bố — bất biến' : 'Đã gửi — khoá'}
          />
        ) : (
          <Chip size="small" variant="outlined" label="Nháp" />
        )}
        {score.deadline_at && !isLocked && (
          <Typography variant="caption" color="text.secondary">
            Deadline: {new Date(score.deadline_at).toLocaleString('vi-VN')}
          </Typography>
        )}
      </Stack>

      <Stack spacing={1.5}>
        {SEMIFINAL_CRITERIA.map((c) => {
          const v = scores[c.key]
          const numeric = typeof v === 'number' ? v : 0
          const overflow = typeof v === 'number' && v > c.max_score
          return (
            <Box key={c.key}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.5}
                alignItems={{ sm: 'flex-start' }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" fontWeight={700} color="primary">
                    {c.name}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mt: 0.25 }}
                  >
                    {c.description}
                  </Typography>
                </Box>
                <TextField
                  type="number"
                  size="small"
                  label={`/ ${c.max_score}`}
                  value={v === '' ? '' : v}
                  onChange={(e) => {
                    const raw = e.target.value
                    const parsed = raw === '' ? '' : Math.max(0, parseFloat(raw) || 0)
                    setScores((prev) => ({ ...prev, [c.key]: parsed }))
                  }}
                  disabled={isLocked}
                  inputProps={{
                    min: 0,
                    max: c.max_score,
                    step: 0.5,
                    style: { textAlign: 'right', fontWeight: 700, width: 70 },
                  }}
                  error={overflow}
                  helperText={overflow ? `Tối đa ${c.max_score}` : ''}
                  sx={{ minWidth: 120 }}
                />
              </Stack>
              <Box sx={{ mt: 0.25 }}>
                <Typography variant="caption" color="text.secondary">
                  Hiện tại: <b>{numeric.toFixed(1)}</b> / {c.max_score}
                </Typography>
              </Box>
            </Box>
          )
        })}
      </Stack>

      <Divider sx={{ my: 2 }} />

      <TextField
        label="Nhận xét (không bắt buộc)"
        value={comments}
        onChange={(e) => setComments(e.target.value)}
        multiline
        minRows={2}
        maxRows={5}
        fullWidth
        size="small"
        disabled={isLocked}
        sx={{ mb: 2 }}
      />

      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
        <Typography variant="h6" fontWeight={800} color={overflows.length > 0 ? 'error' : 'primary'}>
          Tổng: {total.toFixed(2)} / {TOTAL_MAX}
        </Typography>
        {!isLocked && (
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              onClick={handleSaveDraft}
              disabled={saving || submitting}
            >
              {saving ? 'Đang lưu...' : 'Lưu nháp'}
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<CheckCircle />}
              onClick={handleSubmit}
              disabled={saving || submitting || overflows.length > 0}
            >
              {submitting ? 'Đang gửi...' : 'Gửi phiếu'}
            </Button>
          </Stack>
        )}
      </Stack>

      {errMsg && (
        <Alert severity="error" sx={{ mt: 1.5 }}>
          {errMsg}
        </Alert>
      )}
      {okMsg && !errMsg && (
        <Alert severity="success" sx={{ mt: 1.5 }}>
          {okMsg}
        </Alert>
      )}
    </Box>
  )
}
